import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { AdminThemeProvider } from './contexts/AdminThemeContext';
import { useAdminAuth } from './contexts/AdminAuthContext';
import { useAdminTheme } from './contexts/AdminThemeContext';
import ErrorBoundary from './components/ErrorBoundary';

// Components
import AdminSidebar from './components/AdminSidebar';
import AdminNavbar from './components/AdminNavbar';
import LoadingSpinner from './components/LoadingSpinner';

// Pages
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import CandidateManagement from './pages/CandidateManagement';
import EmployerManagement from './pages/EmployerManagement';
import SystemSettings from './pages/SystemSettings';
import AdminSettings from './pages/AdminSettings';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAdminAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAdminAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Admin Layout Component
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <AdminSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AdminNavbar />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-6 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { theme } = useAdminTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300`}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><AdminLogin /></PublicRoute>} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="/users" element={
            <ProtectedRoute>
              <AdminLayout>
                <UserManagement />
              </AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="/candidates" element={
            <ProtectedRoute>
              <AdminLayout>
                <CandidateManagement />
              </AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="/employers" element={
            <ProtectedRoute>
              <AdminLayout>
                <EmployerManagement />
              </AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <AdminLayout>
                <SystemSettings />
              </AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="/admin-settings" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminSettings />
              </AdminLayout>
            </ProtectedRoute>
          } />

          {/* Default Routes */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        <ToastContainer
          position="bottom-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={theme}
        />
      </Router>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AdminThemeProvider>
      <AdminAuthProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </AdminAuthProvider>
    </AdminThemeProvider>
  );
};

export default App;
